import { useEffect, useState } from "react";
import { MediaType } from "@domain/learn/learn-service";
import { calculateMediaDurationMinutes } from "@utils/number-utils";
import { useI18n } from "@core/i18n/i18n";

interface MediaDurationResult {
	durationLabel: string | undefined;
}

const loadVideoDuration = (
	video: HTMLVideoElement,
	apiDuration: number | null | undefined,
	setDuration: (duration: number) => void,
) => {
	const handleLoadedMetadata = () => {
		if (video.duration && Number.isFinite(video.duration) && video.duration > 0) {
			const videoDurationMinutes = calculateMediaDurationMinutes(video.duration);
			const totalDuration = videoDurationMinutes + (apiDuration ?? 0);
			if (totalDuration > 0) {
				setDuration(totalDuration);
			}
		}
	};
	video.addEventListener("loadedmetadata", handleLoadedMetadata);
	return () => {
		video.removeEventListener("loadedmetadata", handleLoadedMetadata);
		video.src = "";
	};
};

export const useMediaDuration = (
	mediaType: MediaType | undefined,
	mediaUrl: string | undefined,
	apiDuration?: number | null,
): MediaDurationResult => {
	const { format } = useI18n();
	const [mediaDurationMinutes, setMediaDurationMinutes] = useState<number | null>(null);
	useEffect(() => {
		// For ARTICLE type, use duration from API
		if (mediaType === MediaType.ARTICLE && apiDuration) {
			setMediaDurationMinutes(apiDuration);
			return;
		}
		// For VIDEO type, calculate duration from video metadata and add apiDuration
		if (mediaType === MediaType.VIDEO && mediaUrl) {
			const video = document.createElement("video");
			video.preload = "metadata";
			video.src = mediaUrl;
			return loadVideoDuration(video, apiDuration, setMediaDurationMinutes);
		}
	}, [mediaType, mediaUrl, apiDuration]);

	const durationLabel =
		mediaDurationMinutes !== null && mediaDurationMinutes <= 1
			? format("global.time.minute")
			: format("global.time.minutes").replace("%d", (mediaDurationMinutes ?? 0).toString());

	return {
		durationLabel,
	};
};
